import {
  findTown,
  placeFileKey,
  searchPlaces,
  townFileKey,
  type PlaceEntry,
} from '@talkofthetown/shared';

/** A place the reader picked to look around, instead of their own location. */
export interface ChosenPlace {
  name: string;
  /** Region and country, e.g. "Kerala, India". */
  detail: string;
  lat: number;
  lng: number;
}

const files = new Map<string, Promise<PlaceEntry[]>>();

/** One file of the town list (web/public/places, built by scripts/build-places.ts). */
function placeFile(key: string): Promise<PlaceEntry[]> {
  let file = files.get(key);
  if (!file) {
    file = fetch(`/places/${key}.json`)
      // No file means no town starts like that (the host answers with the app page instead).
      .then((res) =>
        res.ok && res.headers.get('content-type')?.includes('json')
          ? (res.json() as Promise<PlaceEntry[]>)
          : [],
      )
      .catch(() => {
        files.delete(key); // offline: try again on the next search
        return [];
      });
    files.set(key, file);
  }
  return file;
}

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

export function countryName(code: string): string {
  try {
    return regionNames.of(code) ?? code;
  } catch {
    return code;
  }
}

/** A town from the list, as a place to look around. */
const toChosen = ([name, region, cc, lat, lng]: PlaceEntry): ChosenPlace => ({
  name,
  detail: [region, countryName(cc)].filter((s) => s && s !== name).join(', '),
  lat,
  lng,
});

/** Towns matching what the reader typed, best first. */
export async function findPlaces(query: string, limit = 8): Promise<ChosenPlace[]> {
  const key = placeFileKey(query);
  if (!key) return [];
  return searchPlaces(await placeFile(key), query, countryName, limit).map(toChosen);
}

/** The town at a page address (/town/<slug>, e.g. kochi-in), or null. */
export async function townAt(slug: string): Promise<ChosenPlace | null> {
  const key = townFileKey(slug);
  const entry = key ? findTown(await placeFile(key), slug) : null;
  return entry ? toChosen(entry) : null;
}
